import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, MessageSquare, MessageCircle, Mail, Landmark, PackageOpen, FileText, BellRing } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useAuth, isManagerUp } from "../../lib/auth";
import { LetterDialog } from "../../components/PrintDialog";

export default function Alerts() {
  const { t } = useLang();
  const { user } = useAuth();
  const managerUp = isManagerUp(user);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [letter, setLetter] = useState(null);

  const resolveNotice = async (id) => {
    try {
      await API.post(`/shop/notices/${id}/resolve`);
      toast.success(t("msg.saved"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const openNotice = async (loanId, kind) => {
    try {
      const { data } = await API.get(`/shop/loans/${loanId}/notice`, { params: { kind } });
      setLetter({ doc: data, title: t(`ntc.${kind}`) });
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const load = () => API.get("/shop/alerts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-slate-500">{t("common.loading")}</p>;

  const act = async (loan, action) => {
    try {
      await API.post(`/shop/loans/${loan.id}/${action}`, action === "extend" ? { months: 1 } : undefined);
      toast.success(t("msg.saved"));
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const remind = async (loan, channel) => {
    try {
      const { data } = await API.post(`/shop/loans/${loan.id}/notify`, { channel, lang: localStorage.getItem("pawno_lang") || "en" });
      if (data.status === "failed") {
        toast.error(t("alerts.reminder_failed"), { description: data.message });
      } else {
        toast.success(data.status === "sent" ? t("alerts.reminder_live") : t("alerts.reminder_sent"), { description: data.message });
      }
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const waChat = (l) => {
    const digits = (l.phone || "").replace(/[^0-9]/g, "");
    if (!digits) return;
    const status = l.days_overdue > 0 ? `is ${l.days_overdue} day(s) overdue` : "is due soon";
    const msg = `Hello ${l.customer_name}, your loan ${l.loan_number} ${status}. Total due ${money(l.total_due)}. Please contact us.`;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const LoanCard = ({ l, overdue }) => (
    <div className={`bg-white rounded-xl border p-5 flex items-center justify-between flex-wrap gap-4 ${overdue ? "border-red-200" : "border-amber-200"}`}
      data-testid={`alert-loan-${l.loan_number}`}>
      <div>
        <div className="flex items-center gap-3">
          <span className="font-mono font-bold text-slate-900">{l.loan_number}</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${overdue ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
            {overdue ? `${l.days_overdue} ${t("alerts.days_overdue")}` : `${l.days_until_due} ${t("alerts.days_left")}`}
          </span>
          {overdue && l.stage && (
            <span data-testid={`alert-stage-${l.loan_number}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${l.stage === "mora" ? "bg-amber-100 text-amber-800" : l.stage === "breach" ? "bg-orange-100 text-orange-800" : "bg-red-600 text-white"}`}>
              {t(`alerts.stage_${l.stage}`)}
            </span>
          )}
        </div>
        <div className="text-sm text-slate-500 mt-1">{l.customer_name} · {t("loans.due")}: {fmtDate(l.due_date)}</div>
        <div className="font-mono text-sm font-semibold text-emerald-700 mt-1">{t("loans.total_due")}: {money(l.total_due)}</div>
      </div>
      <div className="flex gap-2">
        <button data-testid={`alert-pay-${l.loan_number}`} onClick={() => navigate(`/app/loans?open=${l.id}`)}
          className="px-3 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
          {t("loans.record_payment")}
        </button>
        {managerUp && (<>
        {overdue && l.stage !== "mora" && (
          <button data-testid={`alert-notice-${l.loan_number}`} onClick={() => openNotice(l.id, "sale_notice")}
            className="px-3 py-2 text-sm rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />{t("ntc.sale_notice")}
          </button>
        )}
        <button data-testid={`alert-extend-${l.loan_number}`} onClick={() => act(l, "extend")}
          className="px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
          +1 {t("loans.extend")}
        </button>
        <button data-testid={`alert-sms-${l.loan_number}`} onClick={() => remind(l, "sms")}
          className="px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors flex items-center gap-1.5">
          <MessageSquare className="h-3.5 w-3.5" />SMS
        </button>
        <button data-testid={`alert-whatsapp-${l.loan_number}`} onClick={() => remind(l, "whatsapp")}
          className="px-3 py-2 text-sm rounded-lg border border-green-300 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1.5">
          <MessageCircle className="h-3.5 w-3.5" />WhatsApp
        </button>
        <button data-testid={`alert-email-${l.loan_number}`} onClick={() => remind(l, "email")}
          className="px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5" />Email
        </button>
        {l.phone && (
          <button data-testid={`alert-chat-${l.loan_number}`} onClick={() => waChat(l)}
            className="px-3 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1.5">
            <MessageCircle className="h-3.5 w-3.5" />{t("alerts.chat")}
          </button>
        )}
        {overdue && l.days_overdue > 10 && (
          <button data-testid={`alert-auction-${l.loan_number}`}
            onClick={() => window.confirm(t("auctions.send") + "?") && act(l, "auction")}
            className="px-3 py-2 text-sm rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 transition-colors">
            {t("auctions.send")}
          </button>
        )}
        <button data-testid={`alert-forfeit-${l.loan_number}`}
          onClick={() => window.confirm(t("loans.forfeit") + "?") && act(l, "forfeit")}
          className="px-3 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
          {t("loans.forfeit")}
        </button>
        </>)}
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="alerts-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("alerts.title")}</h1>
      {data.notice_deadlines?.length > 0 && (
        <section data-testid="notice-deadlines-section">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-red-800 mb-3">
            <BellRing className="h-5 w-5" />{t("alerts.notice_deadlines")} ({data.notice_deadlines.length})
          </h2>
          <div className="bg-red-50 rounded-xl border border-red-200 divide-y divide-red-100">
            {data.notice_deadlines.map((n) => (
              <div key={n.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`notice-deadline-${n.loan_number}`}>
                <div className="flex-1 min-w-48">
                  <div className="font-mono font-bold text-slate-900">{n.loan_number} <span className="font-sans font-normal text-slate-600">· {n.customer_name}</span></div>
                  <div className="text-xs text-slate-600">{t(`ntc.${n.kind}`)} · {n.notice_no} · {t("ntc.deadline")} {n.deadline} · <span className="font-semibold text-red-700">{n.days_past} {t("alerts.days_past")}</span></div>
                  <div className="text-xs text-red-800 mt-0.5">{n.kind === "sale_notice" ? t("alerts.deadline_hint_sale") : t("alerts.deadline_hint_collect")}</div>
                </div>
                <button data-testid={`notice-deadline-open-${n.loan_number}`} onClick={() => navigate(n.kind === "sale_notice" ? "/app/auctions" : "/app/warehouse")}
                  className="px-3 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                  {n.kind === "sale_notice" ? t("prep.title") : t("alerts.view_warehouse")}
                </button>
                {managerUp && (
                  <button data-testid={`notice-deadline-resolve-${n.loan_number}`} onClick={() => resolveNotice(n.id)}
                    className="px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-white transition-colors">
                    {t("alerts.resolve")}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      {data && data.capital_due?.length > 0 && (
        <section className="mb-8" data-testid="capital-due-section">
          <h2 className="font-heading text-lg font-medium text-slate-900 mb-3 flex items-center gap-2">
            <Landmark className="h-5 w-5 text-amber-600" />{t("alerts.capital_due")} ({data.capital_due.length})
          </h2>
          <div className="bg-white rounded-xl border border-slate-200 divide-y">
            {data.capital_due.map((c) => (
              <div key={c.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`capital-due-${c.id.slice(0, 8)}`}>
                <div className="flex-1 min-w-40">
                  <div className="font-medium text-slate-900">{c.source}</div>
                  <div className="text-xs text-slate-500">{t("fin.due_date")}: {c.due_date} · {t("fin.total_interest")}: {money(c.total_interest || 0)}</div>
                </div>
                <div className="font-mono font-semibold">{money(c.amount)}</div>
                {c.days_until_due < 0
                  ? <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-red-700">{-c.days_until_due} {t("alerts.days_late")}</span>
                  : <span className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700">{c.days_until_due} {t("alerts.days_left")}</span>}
                <button data-testid={`capital-finance-${c.id.slice(0, 8)}`} onClick={() => navigate("/app/finance")}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                  {t("alerts.view_finance")}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.count === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center text-emerald-800" data-testid="alerts-empty">
          {t("alerts.empty")}
        </div>
      )}
      {data.overdue.length > 0 && (
        <section>
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-red-700 mb-4">
            <AlertTriangle className="h-5 w-5" />{t("alerts.overdue")} ({data.overdue.length})
          </h2>
          <div className="space-y-3">{data.overdue.map((l) => <LoanCard key={l.id} l={l} overdue />)}</div>
        </section>
      )}
      {data.due_soon.length > 0 && (
        <section>
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-amber-700 mb-4">
            <CalendarClock className="h-5 w-5" />{t("alerts.due_soon")} ({data.due_soon.length})
          </h2>
          <div className="space-y-3">{data.due_soon.map((l) => <LoanCard key={l.id} l={l} />)}</div>
        </section>
      )}
      {data.uncollected?.length > 0 && (
        <section data-testid="uncollected-section">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-slate-700 mb-4">
            <PackageOpen className="h-5 w-5" />{t("alerts.uncollected")} ({data.uncollected.length})
          </h2>
          <div className="bg-white rounded-xl border border-slate-200 divide-y">
            {data.uncollected.map((u) => (
              <div key={u.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`uncollected-${u.loan_number}`}>
                <div className="flex-1 min-w-40">
                  <div className="font-mono font-bold text-slate-900">{u.loan_number} <span className="font-sans font-normal text-slate-500">· {u.customer_name}</span></div>
                  <div className="text-xs text-slate-500">{u.days_since_redeemed} {t("alerts.days_since_redeemed")} · {u.items_pending} {t("loans.items")}</div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${u.stage === "uncollected_90" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                  {t(`alerts.stage_${u.stage}`)}
                </span>
                <button data-testid={`uncollected-notice-${u.loan_number}`} onClick={() => openNotice(u.id, u.stage === "uncollected_90" ? "collection_90" : "collection_15")}
                  className="px-3 py-2 text-sm rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />{t(u.stage === "uncollected_90" ? "ntc.collection_90" : "ntc.collection_15")}
                </button>
                <button data-testid={`uncollected-warehouse-${u.loan_number}`} onClick={() => navigate("/app/warehouse")}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                  {t("alerts.view_warehouse")}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
      <LetterDialog doc={letter?.doc} title={letter?.title} onClose={() => setLetter(null)} testid="notice" onShared={load} />
    </div>
  );
}
